import React from 'react';
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  FunctionalFoodsIcon,
  WarOnSugarIcon,
  StressSleepIcon,
  TargetIcon,
  ChartIcon,
} from './Icons';
import { Bar } from 'react-chartjs-2';

const AnalysisCard = ({ title, items, icon, colorClass }) => {
    // Normalize items to always be an array
    let arr = [];
    if (Array.isArray(items)) {
        arr = items.filter(Boolean);
    } else if (typeof items === 'string' && items.trim() !== '') {
        arr = [items];
    } else if (items && typeof items === 'object' && items !== null) {
        arr = Object.values(items).filter(Boolean);
    }
    if (!arr.length) return null;
    return (
        <div className={`analysis-card ${colorClass}`}>
            <h3 className="analysis-title">
                {icon}
                {title}
            </h3>
            <ul className="analysis-list">
                {arr.map((item, index) => (
                    <li key={index} className="analysis-item">
                        <span className="bullet">•</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const GummyMarketAnalysis = ({ analysis, onClose }) => {
    // Scroll to top when analysis page loads
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!analysis || analysis.error) {
        return (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md mx-4">
                    <div className="text-center">
                        <AlertTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Analysis Error</h2>
                        <p className="text-gray-600">{analysis?.error || "Analysis not available"}</p>
                        <button
                            onClick={onClose}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="gummy-analysis-container">
            {/* Header with Back Button */}
            <div className="analysis-header">
                <div className="container">
                    <button
                        onClick={onClose}
                        className="back-button"
                    >
                        <ArrowLeftIcon />
                        Back to Products
                    </button>
                </div>
            </div>

            <div className="analysis-content">
                {/* Header */}
                <header className="text-center mb-20">
                    <h1 className="analysis-main-title">
                        {analysis.reportTitle || "Gummy Market Blueprint"}
                    </h1>
                    <p className="analysis-subtitle">
                        {analysis.reportSubtitle || "A Strategic Analysis for Vahdam's Market Entry"}
                    </p>
                </header>

                {/* Executive Summary */}
                {analysis.executiveSummary && (
                    <section className="analysis-section">
                        <div className="executive-summary-card">
                            <h2 className="executive-summary-title">
                                Executive Summary
                            </h2>
                            <p className="executive-summary-text">
                                {analysis.executiveSummary.summary || "."}
                            </p>
                            {analysis.executiveSummary.recommendation && (
                                <p className="recommendation-highlight">
                                    The analysis concludes with a strong{" "}
                                    <span className="recommendation-decision">
                                        "{analysis.executiveSummary.recommendation.decision}"
                                    </span>{" "}
                                    recommendation for{" "}
                                    <span className="recommendation-concept">
                                        "{analysis.executiveSummary.recommendation.productConcept}"
                                    </span>
                                    {analysis.executiveSummary.recommendation.details && (
                                        <span>. {analysis.executiveSummary.recommendation.details}</span>
                                    )}
                                </p>
                            )}
                        </div>
                    </section>
                )}

                {/* Market Opportunity Analysis */}
                {analysis.marketOpportunity && (
                    <section className="analysis-section">
                        <h2 className="section-title">
                            1. Market Opportunity Analysis
                        </h2>

                        <div className="market-opportunity-grid">
                            <div className="market-opportunity-card">
                                <h3 className="consumer-trends-title">Dominant Consumer Trends</h3>
                                <div className="trends-list">
                                    {analysis.marketOpportunity.consumerTrends?.map((trend, idx) => (
                                        <div key={idx} className="trend-item">
                                            <div className={`trend-icon ${idx === 0 ? 'trend-icon-green' : idx === 1 ? 'trend-icon-red' : 'trend-icon-purple'}`}>
                                                {idx === 0 ? <FunctionalFoodsIcon className="h-6 w-6" /> :
                                                 idx === 1 ? <WarOnSugarIcon className="h-6 w-6" /> :
                                                 <StressSleepIcon className="h-6 w-6" />}
                                            </div>
                                            <div className="trend-content">
                                                <h4>{trend.title}</h4>
                                                <p>{trend.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {!analysis.marketOpportunity.consumerTrends?.length && (
                                        <p className="text-[#64748b] italic">No consumer trends data available</p>
                                    )}
                                </div>
                            </div>

                            <div className="market-opportunity-card">
                                <h3 className="consumer-trends-title">Market Segmentation & Saturation</h3>
                                <p className="text-[#64748b] mb-8 text-lg leading-relaxed">
                                    {analysis.marketOpportunity.marketSegmentation?.summary ||
                                     "The 'Hair, Skin & Nails' category is highly saturated. The clearest opportunity lies in 'Sleep & Stress,' which shows strong demand with fewer premium, multi-ingredient players."}
                                </p>

                                {/* Market Saturation Chart - Chart.js */}
                                <div className="saturation-chart-container">
                                    <div className="graph-header">
                                        <h4 className="graph-title">📊 Market Saturation Analysis</h4>
                                        <p className="graph-subtitle">Visual breakdown of market opportunity by category</p>
                                    </div>

                                    <div className="chart-canvas-container">
                                        <Bar
                                            data={{
                                                labels: analysis.marketOpportunity.marketSegmentation?.saturationChartData?.map(segment => segment.category) || ['Hair, Skin & Nails', 'Sleep & Stress', 'Energy & Vitality', 'General Wellness', 'Kids Wellness'],
                                                datasets: [{
                                                    label: 'Market Saturation Level',
                                                    data: analysis.marketOpportunity.marketSegmentation?.saturationChartData?.map(segment => segment.saturationLevel) || [90, 65, 60, 75, 50],
                                                    backgroundColor: [
                                                        '#ef4444', // Red Alert
                                                        '#7c3aed', // Purple Accent
                                                        '#f59e0b', // Yellow/Gold
                                                        '#3b82f6', // Blue Accent
                                                        '#00D47F'  // Primary Green
                                                    ],
                                                    borderColor: [
                                                        '#dc2626',
                                                        '#6d28d9',
                                                        '#d97706',
                                                        '#2563eb',
                                                        '#059669'
                                                    ],
                                                    borderWidth: 1,
                                                    borderRadius: 5,
                                                }]
                                            }}
                                            options={{
                                                indexAxis: 'y',
                                                scales: {
                                                    x: {
                                                        beginAtZero: true,
                                                        max: 100,
                                                        title: {
                                                            display: true,
                                                            text: 'Saturation (0=Empty, 100=Saturated)',
                                                            font: { family: 'DM Sans' }
                                                        },
                                                        ticks: { font: { family: 'DM Sans' } }
                                                    },
                                                    y: {
                                                         ticks: { font: { family: 'DM Sans' } }
                                                    }
                                                },
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    },
                                                    tooltip: {
                                                        enabled: true,
                                                        bodyFont: { family: 'DM Sans' },
                                                        titleFont: { family: 'DM Sans' }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* <div className="insight-cards">
                                    <div className="insight-card">
                                        <div className="insight-card-header">
                                            <div className="insight-icon">
                                                <TargetIcon className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h4 className="insight-title">White Space Identified</h4>
                                                <p className="insight-subtitle">Strategic Opportunity</p>
                                            </div>
                                        </div>
                                        <p className="insight-text">
                                            {analysis.marketOpportunity.marketSegmentation?.white_space_identified ||
                                             "Sleep & Stress category shows strong demand with fewer premium players"}
                                        </p>
                                    </div>

                                    <div className="insight-card white-space">
                                        <div className="insight-card-header">
                                            <div className="insight-icon">
                                                <ChartIcon className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h4 className="insight-title">Growth Potential</h4>
                                                <p className="insight-subtitle">Market Expansion</p>
                                            </div>
                                        </div>
                                        <p className="insight-text">
                                            {analysis.marketOpportunity.marketSegmentation?.growth_potential ||
                                             "High growth potential in premium sleep solutions"}
                                        </p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </section>
                )}

                {/* Competitor Teardown */}
                {analysis.competitorTeardown && (
                    <section className="analysis-section">
                        <h2 className="section-title">
                            2. In-Depth Competitor Teardown
                        </h2>

                        <div className="competitor-table-container">
                            <div className="overflow-x-auto">
                                <table className="competitor-table">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="table-header">Brand</th>
                                            <th className="table-header">Product Focus</th>
                                            {/* <th className="table-header">Price/100g (₹)</th> */}
                                            {/* <th className="table-header">Sugar/Serving (g)</th> */}
                                            <th className="table-header">Key Nutrients</th>
                                            <th className="table-header">Core Claims</th>
                                        </tr>
                                    </thead>
                                    <tbody className="table-body">
                                        {analysis.competitorTeardown.competitors?.map((competitor, idx) => (
                                            <tr key={idx}>
                                                <td className="table-cell brand">{competitor.brand}</td>
                                                <td className="table-cell">{competitor.productFocus}</td>
                                                {/* <td className="table-cell">{competitor.pricePer100g_inr || competitor.pricePerUnit_inr}</td> */}
                                                {/* <td className={`table-cell ${
                                                    parseFloat(competitor.sugarPerServing_g || 0) === 0 ? 'sugar-low' :
                                                    parseFloat(competitor.sugarPerServing_g || 0) < 2 ? 'sugar-medium' :
                                                    'sugar-high'
                                                }`}>
                                                    {competitor.sugarPerServing_g}g
                                                </td> */}
                                                <td className="table-cell">{competitor.keyNutrients}</td>
                                                <td className="table-cell">{competitor.coreClaims}</td>
                                            </tr>
                                        ))}
                                        {!analysis.competitorTeardown.competitors?.length && (
                                            <tr>
                                                <td colSpan="6" className="table-cell text-center text-[#64748b] italic">
                                                    No competitor data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {analysis.competitorTeardown.strategicInsights?.length > 0 && (
                            <div className="strategic-insights-card">
                                <h3 className="insights-title">Strategic Insights from Competitor Analysis:</h3>
                                <ul className="insights-list">
                                    {analysis.competitorTeardown.strategicInsights.map((insight, idx) => (
                                        <li key={idx}>{insight}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>
                )}

                {/* Proposed Concepts Evaluation */}
                {analysis.proposedConcepts && (
                    <section className="analysis-section">
                        <h2 className="section-title">
                            3. Identifying the "White Space": Proposed Concepts
                        </h2>
                        <div className="concepts-grid">
                            {analysis.proposedConcepts.concepts?.map((concept, idx) => {
                                const colors = {
                                    0: 'concept-a',
                                    1: 'concept-b',
                                    2: 'concept-c'
                                };
                                const colorClass = colors[idx] || 'concept-a';

                                return (
                                    <div key={idx} className={`concept-card ${colorClass} ${concept.isRecommended ? 'recommended' : ''}`}>
                                        {concept.isRecommended && (
                                            <div className="recommended-badge">
                                                RECOMMENDED
                                            </div>
                                        )}
                                        <div className="concept-header">
                                            <h3 className="concept-title">
                                                {concept.name}
                                            </h3>
                                        </div>
                                        <div className="concept-content">
                                            <p className="concept-description">{concept.description}</p>
                                            {concept.winningFormula?.length > 0 && (
                                                <>
                                                    <h4 className="formula-title">Winning Formula:</h4>
                                                    <ul className="formula-list">
                                                        {concept.winningFormula.map((ingredient, sidx) => (
                                                            <li key={sidx} className="formula-item">
                                                                <strong>{ingredient.split(':')[0]}:</strong> {ingredient.split(':')[1]}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                            {concept.marketPositioning && (
                                                <p className="text-sm text-[#64748b] italic">{concept.marketPositioning}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {!analysis.proposedConcepts.concepts?.length && (
                                <p className="text-[#64748b] italic text-center col-span-full">No proposed concepts data available</p>
                            )}
                        </div>
                    </section>
                )}

                {/* Strategic Recommendation */}
                {analysis.strategicRecommendation && (
                    <section className="analysis-section">
                        <h2 className="section-title">
                            4. Strategic Recommendation: {analysis.strategicRecommendation.title?.replace('Strategic Recommendation: ', '') || "Go with 'Restful Sleep'"}
                        </h2>

                        {analysis.strategicRecommendation.swotAnalysis && (
                            <div className="swot-container">
                                <h3 className="swot-title">
                                    SWOT Analysis for "{analysis.strategicRecommendation.title?.replace('Strategic Recommendation: Go with ', '').replace(/'/g, '') || 'Recommended Concept'}"
                                </h3>
                                <div className="swot-grid">
                                    {analysis.strategicRecommendation.swotAnalysis.strengths?.length > 0 && (
                                        <div className="swot-strengths">
                                            <h4 className="quadrant-title">
                                                <span className="swot-icon strengths-icon">✓</span>
                                                Strengths
                                            </h4>
                                            <ul className="swot-list">
                                                {analysis.strategicRecommendation.swotAnalysis.strengths.map((item, idx) => (
                                                    <li key={idx} className="swot-item">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {analysis.strategicRecommendation.swotAnalysis.weaknesses?.length > 0 && (
                                        <div className="swot-weaknesses">
                                            <h4 className="quadrant-title">
                                                <span className="swot-icon weaknesses-icon">⚠</span>
                                                Weaknesses
                                            </h4>
                                            <ul className="swot-list">
                                                {analysis.strategicRecommendation.swotAnalysis.weaknesses.map((item, idx) => (
                                                    <li key={idx} className="swot-item">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {analysis.strategicRecommendation.swotAnalysis.opportunities?.length > 0 && (
                                        <div className="swot-opportunities">
                                            <h4 className="quadrant-title">
                                                <span className="swot-icon opportunities-icon">📈</span>
                                                Opportunities
                                            </h4>
                                            <ul className="swot-list">
                                                {analysis.strategicRecommendation.swotAnalysis.opportunities.map((item, idx) => (
                                                    <li key={idx} className="swot-item">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {analysis.strategicRecommendation.swotAnalysis.threats?.length > 0 && (
                                        <div className="swot-threats">
                                            <h4 className="quadrant-title">
                                                <span className="swot-icon threats-icon">⚡</span>
                                                Threats
                                            </h4>
                                            <ul className="swot-list">
                                                {analysis.strategicRecommendation.swotAnalysis.threats.map((item, idx) => (
                                                    <li key={idx} className="swot-item">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Go-to-Market Blueprint */}
                {/* {analysis.goToMarketBlueprint && (
                    <section className="analysis-section">
                        <h2 className="section-title">
                            5. Go-to-Market Blueprint: {analysis.goToMarketBlueprint.title?.replace('Go-to-Market Blueprint: ', '') || "'Restful Sleep'"}
                        </h2>

                        <div className="gtm-container">
                            <div className="gtm-grid">
                                <div className="gtm-card">
                                    <h4 className="gtm-card-title">
                                        <PricingIcon className="h-6 w-6" /> Pricing & Positioning
                                    </h4>
                                    <div className="gtm-card-content">
                                        <div className="gtm-item">
                                            <span className="gtm-label">Positioning:</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.pricingAndPositioning?.positioning || "Premium, Efficacious, Natural"}</span>
                                        </div>
                                        <div className="gtm-item">
                                            <span className="gtm-label">Target MRP:</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.pricingAndPositioning?.targetMrp_60count_inr || "₹999 - ₹1199 (60 count)"}</span>
                                        </div>
                                        <div className="gtm-item">
                                            <span className="gtm-label">Gross Margin:</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.pricingAndPositioning?.targetGrossMargin || ">55%"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="gtm-card">
                                    <h4 className="gtm-card-title">
                                        <ChannelIcon className="h-6 w-6" /> Channel Strategy
                                    </h4>
                                    <div className="gtm-card-content">
                                        <div className="gtm-item">
                                            <span className="gtm-label">Phase 1 (Launch):</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.channelStrategy?.phase1_launch || "Vahdam D2C, Amazon"}</span>
                                        </div>
                                        <div className="gtm-item">
                                            <span className="gtm-label">Phase 2 (Scale):</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.channelStrategy?.phase2_scale || "Flipkart, Nykaa, Premium Retail"}</span>
                                        </div>
                                        <div className="gtm-item">
                                            <span className="gtm-label">Phase 3 (Expand):</span>
                                            <span className="gtm-value">{analysis.goToMarketBlueprint.channelStrategy?.phase3_expand || "Subscriptions, Bundles"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="gtm-card">
                                    <h4 className="gtm-card-title">
                                        <MarketingIcon className="h-6 w-6" /> Marketing Hooks
                                    </h4>
                                    <div className="gtm-card-content">
                                        <div className="marketing-hooks">
                                            {analysis.goToMarketBlueprint.marketingHooks?.map((hook, idx) => (
                                                <p key={idx} className="marketing-hook">{hook}</p>
                                            )) || (
                                                <>
                                                    <p className="marketing-hook">"Unwind your day, unlock your night."</p>
                                                    <p className="marketing-hook">"The natural ritual for a calmer mind and deeper sleep."</p>
                                                    <p className="marketing-hook">"Zero sugar. Zero stress. Just pure, restful sleep."</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )} */}
            </div>
        </div>
    );
};

export { AnalysisCard, GummyMarketAnalysis };
